/**
 * "Signed agreement" card on Credit Approved Deals (Dean, 28 Oct 2026): sits next to the fraud check once DocuSign
 * completes, lists what is in the envelope and opens the whole signed envelope as one PDF in the portal.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Download, Eye, FileSignature, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { fetchSignedAgreementPdf, getSignedAgreement } from "@/lib/api";
import { MirrorFilePreview } from "@/pages/applications/MirrorFilePreview";

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : null);

export function SignedAgreementCard({ application }) {
  const appId = application?.id;
  const envelopeId = application?.docusign_envelope_id || "";
  const signed = ["signed", "completed"].includes(application?.docusign_status) && envelopeId && !envelopeId.startsWith("MOCK-");
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [preview, setPreview] = useState(false);

  const load = useCallback(async () => {
    if (!signed) return;
    setLoading(true); setError(null);
    try { setMeta(await getSignedAgreement(appId)); }
    catch (e) { setError(e?.response?.data?.detail || e.message || "Could not reach DocuSign"); }
    finally { setLoading(false); }
  }, [appId, signed]);

  useEffect(() => { load(); }, [load]);
  const fileName = `${application?.application_id || "deal"} - Signed agreement.pdf`;
  const previewFile = useMemo(() => ({ name: fileName, endpoint: `/applications/${appId}/signed-agreement.pdf`, source: "DocuSign" }), [fileName, appId]);

  if (!signed) return null;
  const download = async () => {
    setDownloading(true);
    try {
      const blob = await fetchSignedAgreementPdf(appId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = fileName; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      let detail = e?.message || "Download failed";
      try { if (e?.response?.data instanceof Blob) detail = JSON.parse(await e.response.data.text())?.detail || detail; } catch { /* keep */ }
      toast.error(detail);
    } finally { setDownloading(false); }
  };

  return (
    <div className="mt-3 rounded-lg border border-emerald-200 bg-white p-3 space-y-2.5" data-testid="signed-agreement-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-slate-800">
            <FileSignature className="w-4 h-4 text-emerald-600 shrink-0" />
            <span className="font-medium text-sm">Signed agreement</span>
            {meta?.ready && <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5" data-testid="signed-agreement-status">DocuSign completed</span>}
            {meta && !meta.ready && <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5" data-testid="signed-agreement-status">DocuSign says: {meta.docusign_status}</span>}
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5 truncate" data-testid="signed-agreement-meta">
            Envelope {envelopeId.slice(0, 8)}
            {when(meta?.completed_at || application.docusign_signed_at) ? ` · signed ${when(meta?.completed_at || application.docusign_signed_at)}` : ""}
            {meta?.total_pages ? ` · ${meta.total_pages} pages` : ""}
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Button size="sm" className="h-8 bg-slate-900 hover:bg-slate-800 text-white" onClick={() => setPreview(true)} disabled={loading || (meta && !meta.ready)} data-testid="signed-agreement-preview-btn">
            <Eye className="w-3.5 h-3.5 mr-1.5" /> Preview
          </Button>
          <Button size="sm" variant="outline" className="h-8" onClick={download} disabled={downloading || loading || (meta && !meta.ready)} data-testid="signed-agreement-download-btn">
            {downloading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />} Download
          </Button>
        </div>
      </div>

      {loading && <p className="text-[11px] text-slate-500 flex items-center gap-1.5" data-testid="signed-agreement-loading"><Loader2 className="w-3 h-3 animate-spin" /> Reading the envelope from DocuSign…</p>}
      {error && (
        <div className="flex items-start justify-between gap-2 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2.5 py-1.5" data-testid="signed-agreement-error">
          <span className="flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" /> {error}</span>
          <button type="button" className="underline shrink-0" onClick={load} data-testid="signed-agreement-retry"><RefreshCw className="w-3 h-3 inline mr-0.5" />retry</button>
        </div>
      )}
      {meta?.documents?.length > 0 && (
        <ul className="flex flex-wrap gap-1.5" data-testid="signed-agreement-docs">
          {meta.documents.map((d, i) => (
            <li key={d.document_id} className="text-[11px] text-slate-700 bg-slate-50 border border-slate-200 rounded px-2 py-0.5" data-testid={`signed-agreement-doc-${i}`}>
              {d.name}{d.pages ? <span className="text-slate-400"> · {d.pages}p</span> : null}
            </li>
          ))}
        </ul>
      )}
      {meta && !meta.ready && (
        <p className="text-[11px] text-amber-700" data-testid="signed-agreement-not-ready">The portal has this contract as signed but DocuSign still reports "{meta.docusign_status}" — refresh the status above, the signed copy appears once the envelope completes.</p>
      )}

      {preview && <MirrorFilePreview file={previewFile} onClose={() => setPreview(false)} />}
    </div>
  );
}
